import { z } from 'zod';
import { VIRTUAL_PLATFORMS, checkVirtualLinkRule } from '../location/virtual-platform.js';
import { DateTime } from 'luxon';
import { attendeeRosterResponseSchema, eventInstanceConfigSnapshotSchema } from '../event-instances/schemas.js';
import { offeringTagDisplaySchema } from '../admin/tags/offering-tag-display.js';

// ============================================================================
// Phase 5 Domain 5.1 Feature 1 Task 3: Event Series Schemas
// ============================================================================

// === Enum Schemas ===

/** Release mode — mirrors Prisma ReleaseMode enum (lowercased for API contract) */
export const releaseModeSchema = z.enum(['immediate', 'rolling_days_before']);

/** Day of week codes for RRULE BYDAY */
export const dayOfWeekCodeSchema = z.enum(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']);

/** Series end condition — not persisted (derived from RRULE), used in API contract */
export const endConditionSchema = z.enum(['count', 'until', 'never']);

// === Helper Schemas ===

/**
 * Validate YYYY-MM-DD is a real calendar date (not just format).
 * z.string().date() (Zod 3.23+) validates format AND calendar validity.
 */
const calendarDateSchema = z.string().date(); // Rejects "2026-99-99", "2026-02-30"

/**
 * Validate HH:mm is a real 24h time value.
 * Regex checks format; refine checks hour 0-23 and minute 0-59.
 */
const time24hSchema = z.string()
  .regex(/^\d{2}:\d{2}$/, 'Must be HH:mm format')
  .refine((val) => {
    const [h, m] = val.split(':').map(Number);
    return h >= 0 && h <= 23 && m >= 0 && m <= 59;
  }, 'Must be a valid 24-hour time (00:00–23:59)');

/**
 * Validate string is a valid IANA timezone identifier.
 * Uses Luxon's zone validation — rejects arbitrary strings like "Fake/Zone".
 */
const ianaTimezoneSchema = z.string().refine(
  (val) => {
    try {
      return DateTime.local().setZone(val).isValid;
    } catch {
      return false;
    }
  },
  { message: 'Must be a valid IANA timezone identifier (e.g., "America/New_York", "UTC")' },
);

// === API Request Schemas ===

/**
 * Series recurrence rule definition.
 * Frontend sends this; backend converts to RRULE string for storage.
 * v1: Only WEEKLY frequency supported.
 */
export const seriesRuleSchema = z.object({
  freq: z.literal('WEEKLY'),
  interval: z.number().int().positive(),            // Every N weeks
  byDay: z.array(dayOfWeekCodeSchema).min(1),       // At least one day required
  startDate: calendarDateSchema,                     // YYYY-MM-DD, calendar-validated
  startTime: time24hSchema,                          // HH:mm, value-validated
  endCondition: endConditionSchema,
  count: z.number().int().positive().optional(),     // Required when endCondition = 'count'
  until: calendarDateSchema.optional(),              // Required when endCondition = 'until'
}).superRefine((data, ctx) => {
  // Required conditional fields
  if (data.endCondition === 'count' && data.count === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'count is required when endCondition is "count"',
      path: ['count'],
    });
  }
  if (data.endCondition === 'until' && data.until === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'until is required when endCondition is "until"',
      path: ['until'],
    });
  }
  // Reject mutually incompatible extras
  if (data.endCondition !== 'count' && data.count !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'count must not be provided when endCondition is not "count"',
      path: ['count'],
    });
  }
  if (data.endCondition !== 'until' && data.until !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'until must not be provided when endCondition is not "until"',
      path: ['until'],
    });
  }
});

/**
 * Presentation override fields for series instances.
 * All fields optional — only overridden values are applied.
 */
export const seriesPresentationOverridesSchema = z.object({
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  locationAddress: z.string().nullable().optional(),
  isVirtual: z.boolean().optional(),
  virtualPlatform: z.enum(VIRTUAL_PLATFORMS).nullable().optional(),
  virtualLink: z.string().nullable().optional(),
  virtualPassword: z.string().nullable().optional(),
}).superRefine((overrides, ctx) => {
  // virtual-sessions-finish: the one link rule, validation only. Template mode
  // (a platform with no link still passes) because series dates keep their
  // shipped behavior: they copy the platform and take a link only when sent.
  if (
    overrides.virtualPlatform === undefined &&
    overrides.virtualLink === undefined &&
    overrides.virtualPassword === undefined
  ) return;
  const rule = checkVirtualLinkRule(overrides, 'template');
  if (!rule.ok) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [rule.field], message: rule.message });
}).optional();

/** Individual instance entry for manual-dated series. */
export const manualDatesInstanceSchema = z.object({
  date: calendarDateSchema,
  startTime: time24hSchema,
}).strict();

// === Discriminated Union Variants ===

/**
 * schedule-event-redesign: the composer's proof of WHICH template structure it
 * composed against (`computeTemplateStructureToken`).
 *
 * Presence is also the composer discriminator — see
 * `packages/shared/src/service-admin/template-structure-token.ts` note 2 and
 * `docs/data-model/dm-service-admin-model.md` -> "the structure token".
 */
const templateStructureTokenSchema = z.string().min(1).optional();

/** RRULE variant — recurring series with RRULE-based expansion. */
export const rruleSeriesRequestSchema = z.object({
  mode: z.literal('rrule'),
  templateServiceId: z.string(),
  seriesRule: seriesRuleSchema,
  releaseMode: releaseModeSchema,
  releaseDaysBefore: z.number().int().positive().optional(),
  /**
   * schedule-event-redesign: DURABLE skip exclusions. `YYYY-MM-DD` calendar
   * dates in `timezone` that the rhythm must never materialize — honored by the
   * initial expansion AND by every later horizon-extension sweep, so a skipped
   * date cannot come back. Persisted on `EventSeries.excludedDates`.
   */
  skipDates: z.array(calendarDateSchema).optional(),
  templateStructureToken: templateStructureTokenSchema,
  wizardOverrides: eventInstanceConfigSnapshotSchema.partial().optional(),
  presentationOverrides: seriesPresentationOverridesSchema,
  timezone: ianaTimezoneSchema,
}).strict();

/** Manual dates variant — explicitly listed instances with per-entry date/time. */
export const manualDatesSeriesRequestSchema = z.object({
  mode: z.literal('manual_dates'),
  templateServiceId: z.string(),
  instances: z.array(manualDatesInstanceSchema).min(1),
  publishMode: z.enum(['draft', 'live']),
  templateStructureToken: templateStructureTokenSchema,
  wizardOverrides: eventInstanceConfigSnapshotSchema.partial().optional(),
  presentationOverrides: seriesPresentationOverridesSchema,
  timezone: ianaTimezoneSchema,
}).strict();

/**
 * Schedule series request — POST /api/admin/event-series
 *
 * Discriminated union on `mode`:
 * - `rrule`: Recurring series with RRULE-based expansion, release policy.
 * - `manual_dates`: Explicitly listed instances with publish mode.
 *
 * Cross-field validation (superRefine) runs after discriminant matching:
 * - RRULE: releaseMode ↔ releaseDaysBefore consistency
 * - Manual dates: instance date uniqueness
 */
export const scheduleSeriesRequestSchema = z.discriminatedUnion('mode', [
  rruleSeriesRequestSchema,
  manualDatesSeriesRequestSchema,
]).superRefine((data, ctx) => {
  if (data.mode === 'rrule') {
    if (data.releaseMode === 'rolling_days_before' && data.releaseDaysBefore === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'releaseDaysBefore is required when releaseMode is "rolling_days_before"',
        path: ['releaseDaysBefore'],
      });
    }
    if (data.releaseMode === 'immediate' && data.releaseDaysBefore !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'releaseDaysBefore must not be provided when releaseMode is "immediate"',
        path: ['releaseDaysBefore'],
      });
    }
    // schedule-event-redesign: "all sessions at once" on a rhythm that never
    // ends is incoherent, not merely unusual. A 'never' series is materialized
    // only to the booking horizon and extended by the worker, so IMMEDIATE
    // degenerates into a pseudo-rolling release keyed to the horizon rather than
    // to anything the admin chose. The composer hides the option; this refuses
    // it for every caller so the two cannot drift.
    if (data.releaseMode === 'immediate' && data.seriesRule.endCondition === 'never') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'releaseMode "immediate" cannot be combined with an endCondition of "never" — an unbounded rhythm has no "all at once" to release',
        path: ['releaseMode'],
      });
    }
    // Skip exclusions are RRULE-only (a manual-dated package is an explicit list
    // — the admin removes a date rather than excluding one).
    if (data.skipDates !== undefined) {
      const unique = new Set(data.skipDates);
      if (unique.size !== data.skipDates.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'skipDates must be unique',
          path: ['skipDates'],
        });
      }
    }
  } else if (data.mode === 'manual_dates') {
    const dates = data.instances.map(i => i.date);
    const uniqueDates = new Set(dates);
    if (uniqueDates.size !== dates.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Instance dates must be unique',
        path: ['instances'],
      });
    }
  }
});

// === API Response Schemas ===

/**
 * schedule-event-redesign: ONE created occurrence's committed outcome.
 *
 * The composer's completion summary is server-derived: it lists conflicted rows
 * and links each to its calendar day. Aggregate counts plus an undifferentiated
 * `instanceIds[]` cannot power that — the client would have to re-fetch every id
 * and reconstruct which one conflicted, which is exactly the client-side
 * re-derivation the summary requirement forbids.
 *
 * `status` is the FINAL committed status, after the create-then-upgrade pass and
 * (for manual dates) the whole-package draft fallback.
 */
export const seriesInstanceOutcomeSchema = z.object({
  calendarEventId: z.string(),
  startsAt: z.string(),                              // ISO datetime (UTC)
  status: z.enum(['DRAFT', 'CONFIRMED']),
  hasConflict: z.boolean(),
});

/**
 * Schedule series response — 201 Created
 * Returns instance counts by status for the summary modal.
 */
export const scheduleSeriesResponseSchema = z.object({
  seriesId: z.string(),
  instancesCreated: z.number().int().nonnegative(),
  publishedCount: z.number().int().nonnegative(),   // Conflict-free + release policy allows
  draftCount: z.number().int().nonnegative(),        // Release policy holds back
  conflictedCount: z.number().int().nonnegative(),   // Draft due to active conflicts
  instanceIds: z.array(z.string()),
  /**
   * Per-instance committed outcomes, in chronological order. Same set and order
   * as `instanceIds`, which is RETAINED for the shipped callers that only need
   * the ids.
   */
  instances: z.array(seriesInstanceOutcomeSchema),
  /** schedule-event-redesign: dates the request asked to skip, echoed back as persisted. */
  skippedDates: z.array(z.string()),
  publishBlockedByConflicts: z.boolean().optional(), // Manual dates: live→draft fallback
});

/** POST /api/admin/event-series/:seriesId/publish response */
export const publishSeriesResponseSchema = z.object({
  publishedCount: z.number().int().nonnegative(),
  conflictedCount: z.number().int().nonnegative(),
});

/** POST /api/admin/event-series/:seriesId/unpublish response */
export const unpublishSeriesResponseSchema = z.object({
  unpublishedCount: z.number().int().nonnegative(),
  blockedByBookings: z.boolean(),
});

// === Series Detail Response Schemas ===

/** Per-instance row in the series detail response */
export const eventSeriesInstanceRowSchema = z.object({
  calendarEventId: z.string(),
  date: z.string(),                         // ISO date YYYY-MM-DD in series timezone
  startsAt: z.string(),                     // ISO datetime
  endsAt: z.string(),                       // ISO datetime
  opensAt: z.string().nullable(),           // ISO datetime for rolling release; null when immediately open
  status: z.enum(['DRAFT', 'CONFIRMED', 'CANCELLED']),
  hasConflict: z.boolean(),
  attendanceMarkedCount: z.number().int().nonnegative(), // ATTENDED + NO_SHOW marks (all decisions made)
  attendedCount: z.number().int().nonnegative(),          // ATTENDED-only marks (day-ladder "N of M attended")
  attendanceTotalCount: z.number().int().nonnegative(),
  instanceIndex: z.number().int().positive(),  // 1-based chronological position
});

/** Summary statistics for the series detail response */
export const eventSeriesSummarySchema = z.object({
  totalInstances: z.number().int().nonnegative(),
  statusCounts: z.object({
    draft: z.number().int().nonnegative(),
    confirmed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
  }),
  completedCount: z.number().int().nonnegative(),
  conflictedInstanceCount: z.number().int().nonnegative(),
  upcomingCount: z.number().int().nonnegative(),
});

/** Form summary for series overview (form name + required/optional) */
export const eventSeriesFormSummarySchema = z.object({
  formId: z.string(),
  formName: z.string(),
  isRequired: z.boolean(),
});

/** GET /api/admin/event-series/:seriesId response */
export const eventSeriesDetailResponseSchema = z.object({
  seriesId: z.string(),
  seriesType: z.literal('MANUAL_DATED'),
  title: z.string(),
  practitionerName: z.string(),
  templateServiceId: z.string(),
  templateServiceName: z.string(),
  templateServiceImage: z.string().nullable(),
  customImage: z.string().nullable(),
  focalPointX: z.number().nullable(),
  focalPointY: z.number().nullable(),
  focalPointScale: z.number().min(1).max(3).nullable().optional(),
  maxCapacity: z.number().int().positive(),
  bookedCount: z.number().int().nonnegative(),
  attendance: z.object({
    confirmed: z.number().int().nonnegative(),
    inFlight: z.number().int().nonnegative(),
    activeHolds: z.number().int().nonnegative(),
    takenSeats: z.number().int().nonnegative(),
  }),
  groupTotalPrice: z.number().nullable(),
  roster: attendeeRosterResponseSchema,
  netCollected: z.number().nonnegative(),
  timezone: z.string(),
  configSnapshot: eventInstanceConfigSnapshotSchema,
  instances: z.array(eventSeriesInstanceRowSchema),
  summary: eventSeriesSummarySchema,
  accentVariant: z.enum(['event', 'recurring_event']),
  // Location (from first chronological instance)
  locationName: z.string().nullable(),
  locationAddress: z.string().nullable(),
  locationCity: z.string().nullable(),
  locationState: z.string().nullable(),
  locationCountry: z.string().nullable(),
  locationLat: z.number().nullable(),
  locationLng: z.number().nullable(),
  locationPlaceId: z.string().nullable(),
  locationNotes: z.string().nullable(),
  isVirtual: z.boolean(),
  virtualPlatform: z.string().nullable(),
  virtualLink: z.string().nullable(),
  formSummaries: z.array(eventSeriesFormSummarySchema),
  tags: offeringTagDisplaySchema,
  createdAt: z.string(),
});
